import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TdLoadingService } from '@covalent/core/loading';
import { last } from 'lodash';
import { combineLatest, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { NavigationService } from 'src/app/core/navigation.service';
import { USER } from 'src/app/core/user.service';
import { BaseHttpClient } from 'src/app/shared/flight-log-api/bases/base-client.service';
import {
  DutyLogHttpClient,
  DutyOnParams,
} from 'src/app/shared/flight-log-api/duty-logs/duty-log-client.service';
import { BaseEntity } from 'src/store/base/base.model';
import { filterNull } from 'src/util/rxjs/operators';
import { UserViewModel } from 'src/view-models/user-view-model';
import { DutyOnFormComponent, DutyOnFormParams } from '../duty-on-form/duty-on-form.component';

@Component({
  selector: 'fl-duty-on-page',
  templateUrl: './duty-on-page.component.html',
  styleUrls: ['./duty-on-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DutyOnPageComponent implements OnInit, OnDestroy {
  public loadingKey = 'duty-on-form';
  get pilotName(): string {
    return this.user.fullName;
  }
  public onDestroy$ = new Subject();

  @ViewChild(DutyOnFormComponent)
  public formCmp: DutyOnFormComponent;

  public params$: Observable<DutyOnFormParams>;
  public loading$ = new ReplaySubject<boolean>(1);
  public selectedBase: BaseEntity | undefined;

  constructor(
    @Inject(USER) private user: UserViewModel,
    private dutyLogs: DutyLogHttpClient,
    private _route: ActivatedRoute,
    private _router: Router,
    private _loading: TdLoadingService,
    private _dutyLogClient: DutyLogHttpClient,
    private _nav: NavigationService,
    private _snackBar: MatSnackBar,
    private _baseClient: BaseHttpClient,
  ) {}

  public ngOnInit() {
    const entryId = this._route.snapshot.params.id;
    if (entryId != null) {
      // Redirect to edit if an ID was provided
      this._router.navigate(['duty-logs', entryId]);
    }
    // Redirect to edit if the user is currently on their shift
    this.user.isOnDuty$
      .pipe(
        filter((isOnDuty) => isOnDuty),
        switchMap(() => this.user.latestShift$),
        filterNull(),
        takeUntil(this.onDestroy$),
      )
      .subscribe((currentShift) => {
        this._router.navigate(['duty-logs', currentShift.id]);
      });

    // get last selected aircraft for this base
    const aircraftId$ = this.dutyLogs
      .getMany({
        baseId: this.user.base?.dispatchBaseId ?? this.user.employee.baseId,
        latest: true,
      })
      .pipe(
        map((result) => result.results),
        map((baseDutyLogs) => {
          const lastBaseDutyLog = last(baseDutyLogs);
          return lastBaseDutyLog != null ? lastBaseDutyLog.aircraftId : undefined;
        }),
      );

    this.params$ = this._dutyLogClient
      // Get latest duty logs first
      .getMany({ pilotEmployeeId: this.user.id })
      .pipe(
        map(() => {
          if (this.user.baseId != null) {
            return this._baseClient.getById(this.user.baseId);
          } else {
            return of(null);
          }
        }),
        switchMap((base) => combineLatest([this.user.previousShift$, aircraftId$, base])),
        map(([previous, aircraft, base]) => {
          const userBaseId =
            base != null && base.dispatchBaseId != null ? base.dispatchBaseId : this.user.baseId;
          return {
            aircraftId: aircraft,
            baseId: this.user.flightLogFeatures.dutyLogmanagementForCompany
              ? userBaseId
              : this.user.base != null && this.user.base.enableFlightLogDutyLog
              ? this.user.base.id
              : undefined,
            employeeId: this.user.id,
            prevEntryEndTime: previous != null ? previous.dutyPeriodEndTime : undefined,
          };
        }),
      );
  }

  public async dutyOn() {
    this.loading$.next(true);
    this._loading.register(this.loadingKey);
    try {
      const dutyOn: DutyOnParams = this.formCmp.form.value;
      await this._dutyLogClient.dutyOn(dutyOn).toPromise();
      this._snackBar.open('Successfully started shift.', undefined, {
        duration: 2000,
      });
      const dispatchedByExternalCad = this.selectedBase?.dispatchedByExternalCad ?? false;
      if (this.user.flightLogFeatures.tcsUnitManagement && !dispatchedByExternalCad) {
        this._router.navigate(['units', 'manage-unit'], {
          queryParams: {
            baseId: dutyOn.baseId,
          },
        });
      } else {
        this._nav.goBack();
      }
    } catch (err) {
      this._snackBar.open('Error starting shift.', undefined, {
        duration: 2000,
      });
      throw err;
    } finally {
      this._loading.resolveAll(this.loadingKey);
      this.loading$.next(false);
    }
  }

  public ngOnDestroy() {
    this.onDestroy$.next();
  }

  public onBaseSelected(base: BaseEntity) {
    this.selectedBase = base;
  }
}
